import { useState, useEffect, useCallback, useRef } from 'react';
import { usersApi, premiumApi, User, UpdateUserDto, UserDetails, GetUsersParams } from '../../../api/users';
import { toast } from 'sonner';
import { UseUsersReturn } from '../types';
import { fromTashkentInput, toTashkentInput } from '@/lib/datetime';
import { getErrorMessage } from '@/lib/errors';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { useConfirm } from '@/components/shared/ConfirmDialog';
import { userShortName } from '../format';

const ITEMS_PER_PAGE = 10;
/** Максимум limit у GET /users (GetUsersQueryDto). */
const EXPORT_PAGE_SIZE = 100;

export function useUsers(): UseUsersReturn {
  const [users, setUsers] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(ITEMS_PER_PAGE);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>('all');
  // 'false' — активные (по умолчанию), 'true' — удалённые, 'all' — все
  const [deletionFilter, setDeletionFilter] = useState<string>('false');
  const [isLoading, setIsLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<UpdateUserDto>({});
  const [viewingUserId, setViewingUserId] = useState<string | null>(null);
  const [viewingUser, setViewingUser] = useState<UserDetails | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [isQuickActionBusy, setIsQuickActionBusy] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [restoringUser, setRestoringUser] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [premiumUser, setPremiumUser] = useState<User | null>(null);
  const [isGrantingPremium, setIsGrantingPremium] = useState(false);
  const confirm = useConfirm();
  // id карточки, ответ для которой ещё ждём — защищает от гонки при быстром переключении строк
  const detailsRequestRef = useRef<string | null>(null);

  const buildParams = useCallback((): GetUsersParams => {
    const params: GetUsersParams = { isDeleted: deletionFilter };
    if (searchQuery) params.search = searchQuery;
    if (roleFilter !== 'all') params.role = roleFilter as 'user' | 'admin';
    if (subscriptionFilter !== 'all') params.isSubscribed = subscriptionFilter;
    return params;
  }, [searchQuery, roleFilter, subscriptionFilter, deletionFilter]);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await usersApi.getUsers({ ...buildParams(), page: currentPage, limit: pageSize });
      setUsers(response.users);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (error: unknown) {
      setLoadError(getErrorMessage(error, 'Не удалось загрузить пользователей'));
    } finally {
      setIsLoading(false);
    }
  }, [buildParams, currentPage, pageSize]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const fetchAllForExport = async (): Promise<User[]> => {
    const params = buildParams();
    const first = await usersApi.getUsers({ ...params, page: 1, limit: EXPORT_PAGE_SIZE });
    const rows = [...first.users];
    for (let page = 2; page <= first.totalPages; page++) {
      const res = await usersApi.getUsers({ ...params, page, limit: EXPORT_PAGE_SIZE });
      rows.push(...res.users);
    }
    return rows;
  };

  const loadDetails = useCallback(async (id: string) => {
    detailsRequestRef.current = id;
    setIsLoadingDetails(true);
    setDetailsError(null);
    try {
      const details = await usersApi.getUserDetails(id);
      if (detailsRequestRef.current === id) setViewingUser(details);
    } catch (error: unknown) {
      if (detailsRequestRef.current === id) {
        setDetailsError(getErrorMessage(error, 'Не удалось загрузить данные пользователя'));
      }
    } finally {
      if (detailsRequestRef.current === id) setIsLoadingDetails(false);
    }
  }, []);

  /** После изменения пользователя: обновить список и открытую карточку (если это он). */
  const refreshAfterChange = (userId: string) => {
    fetchUsers();
    if (viewingUserId === userId) void loadDetails(userId);
  };

  // Запрос уходит через 300 мс после последнего символа; страница сбрасывается на первую.
  const applySearch = useDebouncedCallback((value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  });

  const handleSearch = (value: string) => {
    setSearchInput(value);
    applySearch(value);
  };

  const handleRoleFilter = (value: string) => {
    setRoleFilter(value);
    setCurrentPage(1);
  };

  const handleSubscriptionFilter = (value: string) => {
    setSubscriptionFilter(value);
    setCurrentPage(1);
  };

  const handleDeletionFilter = (value: string) => {
    setDeletionFilter(value);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setRoleFilter('all');
    setSubscriptionFilter('all');
    setDeletionFilter('false');
    setCurrentPage(1);
  };

  const setPageSize = (size: number) => {
    setPageSizeState(size);
    setCurrentPage(1);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setEditFormData({
      email: user.email || '',
      username: user.username,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role,
      isSubscribed: user.isSubscribed,
      // В поле datetime-local — время Ташкента; обратно в ISO переводим при сохранении.
      subscribedEnd: toTashkentInput(user.subscribedEnd) || null,
      isBlocked: user.isBlocked,
    });
    setIsEditDialogOpen(true);
  };

  const handleView = (user: User) => {
    setViewingUserId(user.id);
    // Пока грузятся детали, не показываем карточку предыдущего пользователя.
    setViewingUser((prev) => (prev?.id === user.id ? prev : null));
    void loadDetails(user.id);
  };

  const closeView = () => {
    detailsRequestRef.current = null;
    setViewingUserId(null);
    setViewingUser(null);
    setDetailsError(null);
    setIsLoadingDetails(false);
  };

  const reloadDetails = () => {
    if (viewingUserId) void loadDetails(viewingUserId);
  };

  const handleToggleBlock = async (user: User) => {
    if (isQuickActionBusy) return;
    const name = userShortName(user);
    const block = !user.isBlocked;
    const ok = await confirm(
      block
        ? {
            title: `Заблокировать ${name}?`,
            description: 'Пользователь не сможет войти в приложение, пока блокировку не снимут.',
            confirmLabel: 'Заблокировать',
            destructive: true,
          }
        : {
            title: `Разблокировать ${name}?`,
            description: 'Пользователь снова сможет входить в приложение.',
            confirmLabel: 'Разблокировать',
          },
    );
    if (!ok) return;

    setIsQuickActionBusy(true);
    try {
      await usersApi.updateUser(user.id, { isBlocked: block });
      toast.success(block ? 'Пользователь заблокирован' : 'Блокировка снята');
      refreshAfterChange(user.id);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, block ? 'Не удалось заблокировать' : 'Не удалось снять блокировку'));
    } finally {
      setIsQuickActionBusy(false);
    }
  };

  const handleToggleAdmin = async (user: User) => {
    if (isQuickActionBusy) return;
    const name = userShortName(user);
    const makeAdmin = user.role !== 'admin';
    const ok = await confirm(
      makeAdmin
        ? {
            title: `Сделать ${name} администратором?`,
            description:
              'Пользователь получит полный доступ к админке: деньги, рассылки, данные всех пользователей и настройки приложения.',
            confirmLabel: 'Выдать роль админа',
            destructive: true,
          }
        : {
            title: `Снять роль админа с ${name}?`,
            description: 'Пользователь потеряет доступ к админ-панели.',
            confirmLabel: 'Снять роль',
            destructive: true,
          },
    );
    if (!ok) return;

    setIsQuickActionBusy(true);
    try {
      await usersApi.updateUser(user.id, { role: makeAdmin ? 'admin' : 'user' });
      toast.success(makeAdmin ? 'Роль админа выдана' : 'Роль админа снята');
      refreshAfterChange(user.id);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось изменить роль'));
    } finally {
      setIsQuickActionBusy(false);
    }
  };

  const handleSave = async () => {
    if (!editingUser || isSaving) return;

    const email = editFormData.email?.trim();
    // Бэкенд не умеет стирать email: очистка поля молча оставила бы старый адрес.
    if (editingUser.email && !email) {
      toast.error('Email нельзя удалить, укажите новый адрес');
      return;
    }

    // Премиум без даты окончания не виден приложению — не даём его выдать.
    if (editFormData.isSubscribed && !editFormData.subscribedEnd) {
      toast.error('Укажите, до какой даты действует подписка');
      return;
    }

    // Высокорисковые изменения — только после явного подтверждения.
    const userName = userShortName(editingUser);
    if (editFormData.role === 'admin' && editingUser.role !== 'admin') {
      const ok = await confirm({
        title: `Сделать ${userName} администратором?`,
        description:
          'Пользователь получит полный доступ к админке: деньги, рассылки, данные всех пользователей и настройки приложения.',
        confirmLabel: 'Выдать роль админа',
        destructive: true,
      });
      if (!ok) return;
    }
    if (editFormData.isBlocked && !editingUser.isBlocked) {
      const ok = await confirm({
        title: `Заблокировать ${userName}?`,
        description: 'Пользователь не сможет войти в приложение, пока блокировку не снимут.',
        confirmLabel: 'Заблокировать',
        destructive: true,
      });
      if (!ok) return;
    }

    setIsSaving(true);
    try {
      await usersApi.updateUser(editingUser.id, {
        ...editFormData,
        // Пустую строку бэкенд отклоняет как неверный email, а поле необязательное.
        email: email || undefined,
        subscribedEnd: fromTashkentInput(editFormData.subscribedEnd),
      });
      toast.success('Пользователь обновлён');
      setIsEditDialogOpen(false);
      setEditingUser(null);
      refreshAfterChange(editingUser.id);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось обновить пользователя'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingUser || isDeleting) return;

    setIsDeleting(true);
    try {
      await usersApi.deleteUser(deletingUser.id);
      toast.success('Аккаунт удалён');
      const id = deletingUser.id;
      setDeletingUser(null);
      refreshAfterChange(id);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось удалить аккаунт'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestore = async () => {
    if (!restoringUser || isRestoring) return;

    setIsRestoring(true);
    try {
      await usersApi.restoreUser(restoringUser.id);
      toast.success('Аккаунт восстановлен');
      const id = restoringUser.id;
      setRestoringUser(null);
      refreshAfterChange(id);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось восстановить аккаунт'));
    } finally {
      setIsRestoring(false);
    }
  };

  const handleGrantPremium = async (days: number) => {
    if (!premiumUser || isGrantingPremium) return;
    const userName = userShortName(premiumUser);
    const ok = await confirm({
      title: `Выдать Premium пользователю ${userName}?`,
      description:
        `Premium будет действовать ${days} дн. с текущего момента. Текущий срок подписки, если он есть, ` +
        'будет заменён, а счётчики попыток экзаменов обнулены.',
      confirmLabel: 'Выдать Premium',
    });
    if (!ok) return;

    setIsGrantingPremium(true);
    try {
      await premiumApi.activate(premiumUser.id, days);
      toast.success(`Premium выдан на ${days} дн.`);
      const id = premiumUser.id;
      setPremiumUser(null);
      refreshAfterChange(id);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось выдать Premium'));
    } finally {
      setIsGrantingPremium(false);
    }
  };

  return {
    users,
    loadError,
    fetchUsers,
    fetchAllForExport,
    premiumUser,
    setPremiumUser,
    isGrantingPremium,
    handleGrantPremium,
    total,
    totalPages,
    currentPage,
    pageSize,
    isLoading,
    searchQuery: searchInput,
    roleFilter,
    subscriptionFilter,
    deletionFilter,
    editingUser,
    isEditDialogOpen,
    editFormData,
    isSaving,
    viewingUserId,
    viewingUser,
    isLoadingDetails,
    detailsError,
    isQuickActionBusy,
    deletingUser,
    restoringUser,
    isDeleting,
    isRestoring,
    setCurrentPage,
    setPageSize,
    handleSearch,
    handleRoleFilter,
    handleSubscriptionFilter,
    handleDeletionFilter,
    resetFilters,
    handleEdit,
    handleView,
    closeView,
    reloadDetails,
    handleToggleBlock,
    handleToggleAdmin,
    handleSave,
    handleDelete,
    handleRestore,
    setDeletingUser,
    setRestoringUser,
    setIsEditDialogOpen,
    setEditFormData,
  };
}

export { ITEMS_PER_PAGE };
